import { api } from './client';
import type { Page } from './catalogs';

/** ч.1113: декларація платника єдиного податку. values: {код поля XSD: 'грн.коп'}. */
export type SingleTaxFormCode = 'J0103509' | 'F0103309' | 'F0103407';

export interface SingleTaxEsvRow { month: number; base: string; rate: string; amount: string }
export interface SingleTaxEsvAppendix {
  include: boolean;
  rows: SingleTaxEsvRow[];
  category_code?: string;
  period_from?: string;
  period_to?: string;
}
/** ч.1114: Додаток 2 — мінімальне податкове зобов'язання (земельні ділянки). */
export interface SingleTaxMpzPlot {
  cadastre: string; own_ha: string; own_arable_pct: string; rent_ha: string; rent_arable_pct: string;
  ngo: string; ngo_1ha: string; k: string; months: number; rent_20pct: string;
  /** обчислені сервером (read-only у відповіді) */
  mpz_ngo?: string; mpz_noval?: string; total?: string;
}
export interface SingleTaxMpzAppendix {
  include: boolean;
  plots: SingleTaxMpzPlot[];
  paid: Record<string, string>;
  k_default?: string;
}
export interface SingleTaxFields {
  /** ч.1185: «Порядковий № за рік» (HNY) — лише юрособа J0103509; обовʼязкове у XSD. */
  year_seq_no?: number | null;
  staff_count?: number;
  kved?: { code: string; name: string }[];
  wz_months?: number[];
  m081?: boolean;
  m082?: boolean;
}

export interface SingleTaxDeclaration {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  form_code: SingleTaxFormCode;
  report_type: 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
  period_year: number;
  period_quarter: number;
  corrected_period_year: number | null;
  corrected_period_quarter: number | null;
  doc_cnt: number;
  values: Record<string, string>;
  fields: SingleTaxFields;
  appendix_data: { esv?: SingleTaxEsvAppendix; mpz?: SingleTaxMpzAppendix };
  comment: string | null;
  deletion_mark: boolean;
  created_at: string;
}

export interface SingleTaxDeclCheck {
  errors: { form: string; message: string }[];
  warnings: { form: string; message: string }[];
  included_appendices: string[];
}

export const FORM_TITLES: Record<SingleTaxFormCode, string> = {
  J0103509: 'Податкова декларація платника єдиного податку третьої групи (юридичної особи)',
  F0103309: 'Податкова декларація платника єдиного податку – фізичної особи – підприємця',
  F0103407: 'Податкова декларація платника єдиного податку – фізичної особи – підприємця (рік)',
};
export const PERIOD_LABELS: Record<number, string> = {
  1: 'I квартал', 2: 'півріччя', 3: 'три квартали', 4: 'рік',
};

export const singleTaxDeclarations = {
  list: (params?: {
    organization_id?: string; period_year?: number; period_quarter?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) =>
    api.get<Page<SingleTaxDeclaration>>('/single-tax-declarations', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<SingleTaxDeclaration>(`/single-tax-declarations/${id}`).then((r) => r.data),
  create: (p: {
    organization_id: string; period_year: number; period_quarter: number;
    report_type?: string; comment?: string | null;
    corrected_period_year?: number; corrected_period_quarter?: number;
  }) =>
    api.post<SingleTaxDeclaration>('/single-tax-declarations', p).then((r) => r.data),
  update: (id: string, p: {
    report_type?: string; comment?: string | null; document_date?: string;
    values?: Record<string, string>; fields?: SingleTaxFields;
    appendix_data?: { esv?: SingleTaxEsvAppendix; mpz?: SingleTaxMpzAppendix };
  }) =>
    api.patch<SingleTaxDeclaration>(`/single-tax-declarations/${id}`, p).then((r) => r.data),
  recalc: (id: string) =>
    api.post<SingleTaxDeclaration>(`/single-tax-declarations/${id}/recalc`).then((r) => r.data),
  check: (id: string) =>
    api.get<SingleTaxDeclCheck>(`/single-tax-declarations/${id}/check`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/single-tax-declarations/${id}`).then(() => undefined),
  xml: (id: string) =>
    api.get(`/single-tax-declarations/${id}/xml`, { responseType: 'blob' }),
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(
      `/single-tax-declarations/${id}/files`).then((r) => r.data),
  submission: (id: string) =>
    api.get<{ submission_id: string | null; status: string | null;
      receipt_accepted?: boolean | null }>(
      `/single-tax-declarations/${id}/submission`).then((r) => r.data),
};
